import { apiErrorMessage } from '@agos/api-client'
import type { Category, Product } from '@agos/api-client/model'
import { T, formatMoney, tt, useUnsavedWork } from '@agos/shared'
import { BUTTON_PRIMARY, BUTTON_SECONDARY, cn, toast } from '@agos/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ProductChecklist } from './ProductChecklist'
import {
  refreshUpsellReads,
  saveUpsellLinks,
  type UpsellGroup,
  type UpsellItem,
  type UpsellLinksPayload,
} from './upsell-api'
import { elsewhereGroupOf, linkedProductIds, linksPayloadOf, sameSet } from './upsell-rules'

/**
 * One add-on and the products it is offered with - `FRONTEND.md` §9.711, the operator's *"assign multiple products
 * on one upsell item"* (O91).
 *
 * **One save replaces this add-on's links in the chosen group with exactly the ticked products** (§10A.365), so the
 * checklist always shows that group's links: choosing another group reads that group's. A product linked to this
 * add-on under a different group is marked where it is now, because ticking it **moves** it - one pair, one way -
 * and a save that moved something nobody was told about is the kind of change an operator cannot find again.
 */
export function ItemEditor({
  addonProductId,
  item,
  initialGroupId,
  groups,
  products,
  categories,
  onClose,
}: {
  addonProductId: number
  /** Undefined for an add-on picked from the catalogue that is offered with nothing yet. */
  item: UpsellItem | undefined
  initialGroupId: number | null
  groups: readonly UpsellGroup[]
  products: readonly Product[]
  categories: readonly Category[]
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [groupId, setGroupId] = useState<number | null>(initialGroupId)

  /* The stored links of the chosen group, and the ticks seeded from them. Re-seeded when either moves - a group
   * picked in the select, or the read answering again after a save - which is the render-time seeding this
   * module's other editors use (`ProductDialog`'s add-ons), so there is no effect to run one render late. */
  const stored = useMemo(() => new Set(linkedProductIds(item, groupId)), [item, groupId])
  const [ticked, setTicked] = useState<Set<number>>(stored)
  const [seededFrom, setSeededFrom] = useState(stored)
  if (seededFrom !== stored) {
    setSeededFrom(stored)
    setTicked(stored)
  }

  const dirty = !sameSet(ticked, stored)
  useUnsavedWork(dirty)

  const save = useMutation({
    mutationFn: (payload: UpsellLinksPayload) => saveUpsellLinks(addonProductId, payload),
    onSuccess: async () => {
      toast({ title: tt('Uložené'), description: tt('Doplnok sa ponúka pri zaškrtnutých produktoch.') })
      await refreshUpsellReads(queryClient)
    },
    onError: (caught) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Väzby doplnku sa nepodarilo uložiť.')),
      }),
  })

  const addon = products.find((product) => product.id === addonProductId)
  const excluded = useMemo(() => new Set([addonProductId]), [addonProductId])
  const groupName = (id: number | null) =>
    id === null ? tt('bez skupiny') : (groups.find((group) => group.id === id)?.name ?? `#${String(id)}`)

  return (
    <section
      className="space-y-3 rounded-2xl border border-border bg-card p-4"
      data-upsell-editor={addonProductId}
      aria-labelledby="upsell-editor-title"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <h2 id="upsell-editor-title" className="truncate text-base font-bold">
            {addon?.name ?? `#${String(addonProductId)}`}
          </h2>
          <p className="text-xs text-muted-foreground">
            <T>Cena doplnku</T> {formatMoney(addon?.price ?? 0)}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={cn('inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm', BUTTON_SECONDARY)}
          data-upsell-editor-close
        >
          <X className="w-4 h-4" />
          <T>Zavrieť</T>
        </button>
      </div>

      <label className="block space-y-1 text-sm">
        <span className="font-semibold">
          <T>Skupina</T>
        </span>
        <select
          value={groupId === null ? '' : String(groupId)}
          onChange={(event) => setGroupId(event.target.value === '' ? null : Number(event.target.value))}
          className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
          data-upsell-editor-group
        >
          <option value="">{tt('Bez skupiny - ponúkne sa ako doteraz')}</option>
          {groups.map((group) => (
            <option key={group.id} value={String(group.id)}>
              {group.name}
              {group.active ? '' : ` (${tt('vypnutá')})`}
            </option>
          ))}
        </select>
        <span className="block text-xs text-muted-foreground">
          <T>Zoznam nižšie ukazuje produkty, pri ktorých sa doplnok ponúka v tejto skupine.</T>
        </span>
      </label>

      <ProductChecklist
        products={products}
        categories={categories}
        excluded={excluded}
        selected={ticked}
        onChange={setTicked}
        marker="upsell"
        note={(product) => {
          const elsewhere = elsewhereGroupOf(item, product.id as number, groupId)
          if (elsewhere === undefined) return null
          return (
            <span
              className="shrink-0 rounded-md bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-600"
              data-upsell-elsewhere={product.id}
              title={tt('Zaškrtnutím sa presunie do tejto skupiny.')}
            >
              <T>teraz</T>: {groupName(elsewhere)}
            </span>
          )
        }}
      />

      <div className="flex flex-wrap items-center justify-end gap-2">
        {dirty && (
          <span className="text-xs font-semibold text-amber-600" data-upsell-editor-unsaved>
            <T>Neuložené zmeny</T>
          </span>
        )}
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate(linksPayloadOf(groupId, ticked))}
          className={cn('inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm', BUTTON_PRIMARY)}
          data-upsell-save-links
        >
          <Save className="w-4 h-4" />
          <T>Uložiť</T>
        </button>
      </div>
    </section>
  )
}
